import { useState, useMemo, useEffect } from 'react';
import { toast } from 'sonner';
import { MagnifyingGlass, Link, X, Sparkle, Plus } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { itApi } from './itApi';
import { CategoryChip } from './categoryColor';
import { suggestMatches } from './assetMatcher';

const SKIP_LS = 'it_omada_skip_automatch_v1';
export const shouldSkipAutoMatch = () => { try { return localStorage.getItem(SKIP_LS) === '1'; } catch { return false; } };
const setSkipAutoMatch = (on) => { try { localStorage.setItem(SKIP_LS, on ? '1' : '0'); } catch { /* ignore */ } };

// Omada 未登録機器を既存 資産 に紐付ける — MAC/IP を PATCH するだけ。
// 次回同期で自動的にマッチして、フロアマップの自動配線も走る。
// onCreateNew を渡すと「新規登録として続行」ボタンが表示され、+登録 フローへ移行。
export default function LinkOmadaDialog({ open, onClose, target, onLinked, onCreateNew, suggestionsOnly }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [skipNext, setSkipNext] = useState(false);
  useEffect(() => { if (open) setSkipNext(false); }, [open]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    itApi.assets().then((rows) => setAssets(Array.isArray(rows) ? rows : []))
      .finally(() => setLoading(false));
  }, [open]);

  // 類似候補（検索していない時のみ表示 — 検索中は明示的な結果を優先）
  const suggestions = useMemo(() => {
    if (search.trim()) return [];
    if (!target?.name || !assets.length) return [];
    const pool = assets.filter((a) => a.type !== 'consumable' && a.status !== 'disposed');
    return suggestMatches(target.name, pool, { limit: 4, threshold: 0.6 });
  }, [assets, target, search]);
  const suggestionIds = useMemo(() => new Set(suggestions.map((s) => s.asset.id)), [suggestions]);

  // MACなし → 先頭。他は名前昇順。候補に出ているものはメイン一覧から除外（重複表示を防ぐ）
  const filtered = useMemo(() => {
    const norm = (s) => String(s || '').toLowerCase();
    const q = norm(search);
    const list = assets.filter((a) => a.type !== 'consumable' && a.status !== 'disposed');
    const withQ = q ? list.filter((a) => (
      norm(a.name).includes(q) ||
      norm(a.asset_tag).includes(q) ||
      norm(a.serial).includes(q) ||
      norm(a.mac_address).includes(q)
    )) : list.filter((a) => !suggestionIds.has(a.id));
    return withQ.sort((a, b) => {
      const am = a.mac_address ? 1 : 0;
      const bm = b.mac_address ? 1 : 0;
      if (am !== bm) return am - bm; // MACなしを先に
      return String(a.name || '').localeCompare(String(b.name || ''), 'ja');
    });
  }, [assets, search, suggestionIds]);

  const doLink = async (asset) => {
    if (!target?.mac) return;
    if (asset.mac_address && asset.mac_address.toLowerCase() !== target.mac.toLowerCase()) {
      if (!window.confirm(`「${asset.name}」には既に別のMAC (${asset.mac_address}) が登録されています。上書きしますか？`)) return;
    }
    setSaving(true);
    try {
      const body = { mac_address: target.mac };
      if (target.ip) body.ip_address = target.ip;
      await itApi.updateAsset(asset.id, body);
      toast.success(`「${asset.name}」に紐付けました`);
      onLinked?.();
      onClose();
    } catch (e) {
      toast.error(`紐付けに失敗しました: ${e?.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-hidden flex flex-col" aria-describedby={undefined} data-testid="link-omada-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5">
            <Link size={17} weight="bold" className="text-sky-600" />
            {suggestionsOnly ? '既存の資産ではありませんか？' : '既存の資産に紐付け'}
          </DialogTitle>
          {suggestionsOnly && (
            <p className="text-xs text-zinc-500 mt-1">
              以下の資産と名前が似ています。既に登録済みの機器なら「紐付け」を選ぶと重複を防げます。
            </p>
          )}
        </DialogHeader>

        {target && (
          <div className="rounded-lg border border-sky-100 bg-sky-50/60 p-2.5 text-xs">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge className="bg-sky-100 text-sky-800 border-sky-200 hover:bg-sky-100">Omada</Badge>
              <span className="font-semibold text-zinc-800">{target.name || target.mac}</span>
              {target.connection && <span className="text-zinc-500">{target.connection}</span>}
            </div>
            <p className="mt-1 font-mono text-[10px] text-zinc-500">MAC: {target.mac}{target.ip ? ` / IP: ${target.ip}` : ''}</p>
          </div>
        )}

        <div className="relative shrink-0">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="link-omada-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="資産名・タグ・シリアル・MACで検索"
            className="pl-9 h-10 text-sm" />
        </div>

        <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-3">
          {suggestions.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-2" data-testid="link-omada-suggestions">
              <p className="text-[10px] font-semibold text-amber-800 flex items-center gap-1 px-1 mb-1">
                <Sparkle size={11} weight="fill" className="text-amber-500" /> 類似する資産（自動候補）
              </p>
              <ul className="divide-y divide-amber-100">
                {suggestions.map(({ asset: a, score }) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => doLink(a)}
                      className="w-full text-left px-2 py-1.5 hover:bg-amber-100/60 rounded-md transition-colors disabled:opacity-50"
                      data-testid={`link-omada-suggest-${a.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-zinc-800 truncate flex-1">{a.name}</span>
                        <span className="text-[9px] font-bold text-amber-600 tabular-nums shrink-0" title="類似度">
                          {Math.round(score * 100)}%
                        </span>
                        {a.asset_tag && <span className="font-mono text-[10px] text-zinc-400 shrink-0">{a.asset_tag}</span>}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                        {a.category && <CategoryChip category={a.category} />}
                        {a.location && <span className="text-[10px] text-zinc-500">📍 {a.location}</span>}
                        {a.mac_address ? (
                          <span className="text-[10px] font-mono text-amber-600" title="別のMACが登録されています">MAC: {a.mac_address}</span>
                        ) : (
                          <span className="text-[10px] text-emerald-600 font-semibold">MAC未登録</span>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {loading ? (
            <p className="text-center text-zinc-400 text-sm py-8">読込中…</p>
          ) : filtered.length === 0 && suggestions.length === 0 ? (
            <p className="text-center text-zinc-400 text-sm py-8">該当する資産がありません</p>
          ) : suggestionsOnly && !search.trim() ? null : filtered.length === 0 ? null : (
            <div>
              {suggestions.length > 0 && (
                <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide px-1 pt-1 pb-1">その他の資産</p>
              )}
              <ul className="divide-y divide-zinc-200">
                {filtered.slice(0, 200).map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => doLink(a)}
                      className="w-full text-left px-2.5 py-2 hover:bg-sky-50/60 rounded-md transition-colors disabled:opacity-50"
                      data-testid={`link-omada-pick-${a.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-zinc-800 truncate flex-1">{a.name}</span>
                        {a.asset_tag && <span className="font-mono text-[10px] text-zinc-400 shrink-0">{a.asset_tag}</span>}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                        {a.category && <CategoryChip category={a.category} />}
                        {a.location && <span className="text-[10px] text-zinc-500">📍 {a.location}</span>}
                        {a.mac_address ? (
                          <span className="text-[10px] font-mono text-amber-600" title="別のMACが登録されています">MAC: {a.mac_address}</span>
                        ) : (
                          <span className="text-[10px] text-emerald-600 font-semibold">MAC未登録</span>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
              {filtered.length > 200 && <p className="text-[10px] text-zinc-400 text-center py-2">先頭200件のみ表示・検索で絞り込んでください</p>}
            </div>
          )}
        </div>

        <DialogFooter className="flex-row justify-between sm:justify-between gap-2 items-center">
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} data-testid="link-omada-cancel-btn">
              <X size={14} className="mr-1" /> キャンセル
            </Button>
            {suggestionsOnly && (
              <label className="flex items-center gap-1.5 text-[11px] text-zinc-500 cursor-pointer select-none" title="今後、+登録時に類似候補ダイアログをスキップします（設定→戻すこともできます）">
                <Checkbox checked={skipNext} onCheckedChange={(v) => setSkipNext(!!v)} data-testid="link-omada-skip-toggle" />
                今後は表示しない
              </label>
            )}
          </div>
          {onCreateNew && (
            <Button onClick={() => { if (skipNext) setSkipAutoMatch(true); onCreateNew(); onClose(); }} className="bg-teal-700 hover:bg-teal-800" data-testid="link-omada-create-new-btn">
              <Plus size={14} weight="bold" className="mr-1" /> 新規登録として続行
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
